/**
 * GuestInfoBottomSheet Component
 * Bottom sheet for collecting guest client information during booking flow.
 * Collects name and phone, then resolves/creates client via API.
 * Features: Brazilian phone formatting, validation, luxury design
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, User, Phone, Loader2, AlertCircle } from 'lucide-react';
import { useWizardStore } from '../../stores/wizardStore';
import { resolveGuestClient } from '../../services/clientService';

const GuestInfoBottomSheet = ({ isVisible, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);

  const nameInputRef = useRef(null);

  const { setClientData, goToNextStep } = useWizardStore();

  // Focus name input when bottom sheet opens
  useEffect(() => {
    if (isVisible && nameInputRef.current) {
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 300); // Wait for animation
    }
  }, [isVisible]);

  // Reset form when closing
  useEffect(() => {
    if (!isVisible) {
      setError(null);
      setNameError(null);
      setPhoneError(null);
    }
  }, [isVisible]);

  // Format phone number as Brazilian format: (XX) XXXXX-XXXX
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Limit to 11 digits
    const limited = digits.slice(0, 11);

    // Apply Brazilian phone formatting
    if (limited.length <= 2) {
      return limited.length > 0 ? `(${limited}` : '';
    } else if (limited.length <= 7) {
      return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
    } else {
      return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    setPhoneError(null);
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    setNameError(null);
  };

  const validateForm = () => {
    let valid = true;

    // Validate name
    if (!name.trim() || name.trim().length < 2) {
      setNameError('Nome deve ter pelo menos 2 caracteres');
      valid = false;
    }

    // Validate phone - Brazilian format requires 10-11 digits
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      setPhoneError('Telefone inválido. Use DDD + 8 ou 9 dígitos');
      valid = false;
    }

    return valid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError(null);

    try {
      // Call the guest checkout API
      const result = await resolveGuestClient({
        name: name.trim(),
        phone: phone.trim()
      });

      // Update wizard store with client data
      setClientData({
        id: result.id,
        name: result.full_name,
        phone: result.phone_number,
        email: result.email || '',
        isNewClient: result.was_created
      });

      // Proceed to next step (confirmation)
      goToNextStep();

      // Notify parent of success
      if (onSuccess) {
        onSuccess(result);
      }

      // Close the bottom sheet
      onClose();

    } catch (err) {
      console.error('Error resolving guest client:', err);
      setError(err.message || 'Erro ao processar seus dados. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVisible && !isLoading) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isVisible, isLoading, onClose]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 dark:bg-black/80 backdrop-blur-sm transition-opacity duration-300"
        onClick={handleBackdropClick}
      />

      {/* Bottom Sheet */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-brand-light dark:bg-brand-dark border-t border-brand-dark/10 dark:border-brand-light/10 transition-transform duration-300 ease-out ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          maxHeight: '85vh'
        }}
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-brand-dark/20 dark:bg-brand-light/20 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-dark/10 dark:border-brand-light/10">
          <div>
            <h2 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
              Seus Dados
            </h2>
            <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 mt-1 tracking-wide">
              Informe seu nome e telefone para continuar
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-2 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300 disabled:opacity-50"
            aria-label="Fechar"
          >
            <X size={22} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 200px)' }}>
          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="flex items-start space-x-3">
                <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}

          {/* Name Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 tracking-wide">
              Nome Completo
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                <User size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
              </div>
              <input
                ref={nameInputRef}
                type="text"
                value={name}
                onChange={handleNameChange}
                placeholder="Seu nome completo"
                disabled={isLoading}
                className={`w-full pl-12 pr-4 py-4 bg-transparent border text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light focus:border-brand-dark dark:focus:border-brand-light tracking-wide transition-all duration-300 disabled:opacity-50 ${
                  nameError
                    ? 'border-red-500 dark:border-red-400'
                    : 'border-brand-dark/20 dark:border-brand-light/20'
                }`}
              />
            </div>
            {nameError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{nameError}</p>
            )}
          </div>

          {/* Phone Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-brand-dark dark:text-brand-light mb-2 tracking-wide">
              Telefone (WhatsApp)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                <Phone size={18} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
              </div>
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="(XX) XXXXX-XXXX"
                disabled={isLoading}
                className={`w-full pl-12 pr-4 py-4 bg-transparent border text-brand-dark dark:text-brand-light placeholder-brand-dark/40 dark:placeholder-brand-light/40 focus:ring-2 focus:ring-brand-dark dark:focus:ring-brand-light focus:border-brand-dark dark:focus:border-brand-light tracking-wide transition-all duration-300 disabled:opacity-50 ${
                  phoneError
                    ? 'border-red-500 dark:border-red-400'
                    : 'border-brand-dark/20 dark:border-brand-light/20'
                }`}
              />
            </div>
            {phoneError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">{phoneError}</p>
            )}
            <p className="mt-2 text-xs text-brand-dark/50 dark:text-brand-light/50">
              Usaremos seu WhatsApp para enviar confirmações
            </p>
          </div>

          {/* Info Notice */}
          <div className="mb-6 p-4 bg-brand-dark/5 dark:bg-brand-light/5 border border-brand-dark/10 dark:border-brand-light/10">
            <p className="text-sm text-brand-dark/70 dark:text-brand-light/70 leading-relaxed">
              Se você já agendou conosco antes, vamos encontrar seu cadastro automaticamente pelo telefone.
            </p>
          </div>
        </div>

        {/* Footer with Button - extra bottom padding for mobile nav */}
        <div className="px-6 py-6 pb-20 border-t border-brand-dark/10 dark:border-brand-light/10">
          <button
            onClick={handleSubmit}
            disabled={isLoading || !name.trim() || !phone.trim()}
            className={`w-full py-5 border font-medium uppercase tracking-widest text-sm transition-all duration-300 flex items-center justify-center ${
              isLoading || !name.trim() || !phone.trim()
                ? 'bg-brand-dark/10 dark:bg-brand-light/10 text-brand-dark/40 dark:text-brand-light/40 border-brand-dark/10 dark:border-brand-light/10 cursor-not-allowed'
                : 'bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border-brand-dark dark:border-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <Loader2 size={18} className="animate-spin" />
                <span>Processando...</span>
              </div>
            ) : (
              'Continuar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuestInfoBottomSheet;
